<script>
// Components
import appConfig from "@/app.config.json";
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import UserProfile from "@/router/views/users/user-profile"

// Helpers
import UserUtils from '@/helpers/users';
import DateUtils from '@/helpers/date';
import Storage from '@/helpers/storage';
import {authComputed} from "@/state/helpers";

// Services
import TicketsDataService from "@/services/TicketsDataService";
import UsersDataService from "@/services/UsersDataService"

// External components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox'
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css'

// Libs
import isEmpty from "is-empty";

export default {
  name: "tickets",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Tickets Manager",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    DatePicker,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },
  computed: {
    ...authComputed
  },
  methods: {
    ...UserUtils,
    ...DateUtils,

    handlePageChange(value) {
      this.page = value;
      this.loadTickets();
    },

    handleDisplayChange() {
      this.loadTickets();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2021-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "today":
          this.filters.fromDate = now.format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          this.filters.toDate = this.filters.fromDate;
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getContactTypeIcon(type) {
      return require(`@/assets/images/support_type_${type}.png`)
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      }
      else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['ticketType'] = this.filters.ticketType.selected;
      params['status'] = this.filters.status.selected;

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    reviewTicket(ticketId) {
      this.$router.push({
        name: 'ticketDetails',
        params: { ticketId }
      });
    },

    reopenTicket(ticket) {
      const params = {
        adminId: this.getUser.uid
      };
      TicketsDataService.reopen(ticket.ticketId, params)
          .then(() => {
            this.$toast.success(this.$t("tickets.reopen_success"));
            this.stats.closed--;
            this.stats.reviewed++;
            ticket.status = "R";
            ticket.closedAt = null;
            this.reviewTicket(ticket.ticketId)

          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(error.toString());
          });
    },

    closeTicket(ticket) {
      const params = {
        adminId: this.getUser.uid
      };
      TicketsDataService.close(ticket.ticketId, params)
          .then(() => {
            this.$toast.success(this.$t("tickets.close_success"));
            this.stats.closed++;
            if (ticket.status === 'R') {
              this.stats.reviewed--;
            } else if (ticket.status === 'W') {
              this.stats.waiting--;
            } else if (ticket.status === 'A') {
              this.stats.answered--;
            }
            ticket.status = "C";
            ticket.closedAt = this.formatDateString(null, "lll");

          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(error.toString());
          });
    },

    getMediaThumb() {
      if (this.tickets.length > 0)
        this.tickets.forEach(ticket => {
          if (ticket.photo && ticket.photo.length > 0)
            Storage.getUserMedia(ticket.userId, ticket.photo, 'ticket-user-'+ticket.ticketId);

        });
    },

    processFilters() {
      this.loadTickets();
      this.loadStats();
    },

    loadStats() {
      const params = this.getRequestParams();
      TicketsDataService
          .getStats(params)
          .then(response => {
            const { stats, ticketTypes, evolGraph } = response.data;
            this.stats = stats;

            this.typesGraph.series = ticketTypes.series;
            this.typesGraph.chartOptions = {...this.typesGraph.chartOptions, ...{
                labels: ticketTypes.labels
            }};

            this.$refs.ticketsEvolChart.updateSeries([{
              name: "Created",
              type: 'bar',
              data: evolGraph.created
            },{
              name: "Closed",
              type: 'bar',
              data: evolGraph.closed
            }, {
              name: "Running",
              type: 'bar',
              data: evolGraph.running
            }]);
          })
          .catch(error => {
            window.console.error(error);
          });
    },

    loadTickets() {
      this.isLoading = true;
      const params = this.getRequestParams();

      TicketsDataService
          .getList(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.tickets = items;
            this.count = totalItems;
            this.isLoading = false;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("tickets.errorNoResult"));
            this.isLoading = false;
          });
    },
    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      if (isEmpty(userId))
        return;

      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user '+userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        }
        catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = userId; }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    },
  },
  mounted() {
    if (this.getRole === 'operator') {
      this.filters.status.selected = '!C';
    }
    this.processFilters();
  },
  data() {
    return {
      isLoading: false,
      title: this.$t("menuitems.support.tickets"),
      typesGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'tickets-type',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },
      evolGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: true,
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
            },
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true
            },
            title: {
              text: "Nb"
            }
          }],
          colors: ["#f46a6a", "#34c38f", "#f1b44c"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      stats: {
        opened: '-',
        reviewed: '-',
        closed: '-',
        waiting: '-',
        answered: '-',
        avgClosedTime: '-',
      },
      tickets: [],
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '20'
      },
      filters: {
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'last30Day',
          options: [
            {value:'today', text:this.$t("dates.today")},
            {value:'yesterday', text:this.$t("dates.yesterday")},
            {value:'week', text:this.$t("dates.week")},
            {value:'curMonth', text:this.$t("dates.curMonth")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'lastMonth', text:this.$t("dates.lastMonth")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        ticketType: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("tickets.type_all")},
            {value: 'vip', text: this.$t("tickets.type_vip")},
            {value: 'bug', text: this.$t("tickets.type_bug")},
            {value: 'account', text: this.$t("tickets.type_account")},
            {value: 'other', text: this.$t("tickets.type_other")},
          ]
        },
        status: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("tickets.status_all")},
            {value: '!C', text: this.$t("tickets.status_not_closed")},
            {value: 'O', text: this.$t("tickets.status_O")},
            {value: 'R', text: this.$t("tickets.status_R")},
            {value: 'C', text: this.$t("tickets.status_C")},
            {value: 'W', text: this.$t("tickets.status_W")},
            {value: 'A', text: this.$t("tickets.status_A")},
          ]
        }
      },
      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Filters -->
      <div class="card">
        <div class="card-body row">
          <b-form inline class="row gy-2 gx-3 align-items-center" id="ticketsFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="ticketsDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>

            </div>

            <!-- TicketType -->
            <div class="col-sm-auto">
              <b-form-select
                  id="ticketsTypeFilter"
                  v-model="filters.ticketType.selected"
                  class="form-select me-lg-1"
                  >
                <option v-for="ct in filters.ticketType.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="col-sm-auto">
              <b-form-select
                  id="ticketsStatusFilter"
                  v-model="filters.status.selected"
                  class="form-select me-lg-1"
                  >
                <option v-for="st in filters.status.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Stats Plain -->
      <div class="row">
        <!-- Opened -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("tickets.opened") }}</p>
                  <h5 class="mb-0">{{ stats.opened }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-danger rounded-circle white font-size-20"
                  >
                    <i class="bx bxs-file-plus"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Reviewed -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid ">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("tickets.reviewed") }}</p>
                  <h5 class="mb-0">{{ stats.reviewed }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-warning text-white rounded-circle font-size-20"
                  >
                    <i class="bx bxs-file"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Waiting -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid ">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("tickets.waiting") }}</p>
                  <h5 class="mb-0">{{ stats.waiting }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-info text-white rounded-circle font-size-20"
                  >
                    <i class="bx bx-mail-send"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Answered -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid ">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("tickets.answered") }}</p>
                  <h5 class="mb-0">{{ stats.answered }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-pink text-white rounded-circle font-size-20"
                  >
                    <i class="bx bx-import"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Closed -->
        <div class="col-lg-2">
            <div class="card blog-stats-wid">
              <div class="card-body">
                <div class="d-flex flex-wrap">
                  <div class="me-3">
                    <p class="text-muted mb-2">{{ $t("tickets.closed") }}</p>
                    <h5 class="mb-0">{{ stats.closed }}</h5>
                  </div>

                  <div class="avatar-sm ms-auto">
                    <div
                        class="avatar-title bg-success rounded-circle text-white font-size-20"
                    >
                      <i class="bx bxs-badge-check"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        <!-- AVG Time -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("tickets.avgClosedTime") }}</p>
                  <h5 class="mb-0">{{ stats.avgClosedTime }}h</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-light rounded-circle text-body font-size-20"
                  >
                    <i class="bx bxs-timer"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row">
        <!-- type -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardTicketsTypeChart">
              <h5 class="card-title">Types</h5>
              <apexchart
                  ref="ticketsTypesChart"
                  height="400"
                  type="donut"
                  dir="ltr"
                  :series="typesGraph.series"
                  :options="typesGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
        <!-- evol -->
        <div class="col-12 col-lg-8">
          <div class="card">
            <div class="card-body" id="cardTicketsEvolChart">
              <h5 class="card-title">Evolution</h5>
              <apexchart
                  ref="ticketsEvolChart"
                  height="350"
                  type="bar"
                  dir="ltr"
                  :series="evolGraph.series"
                  :options="evolGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="tickets-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row" v-if="!isLoading && tickets.length > 0">
        <div class="table-responsive">
        <table
            id="tickets-list"
            class="table project-list-table table-nowrap table-centered table-borderless align-middle"
        >
          <thead>
          <tr>
            <th scope="col">{{ $t('tickets.createdAt') }}</th>
            <th scope="col">{{ $t('tickets.type') }}</th>
            <th scope="col">{{ $t('tickets.user') }}</th>
            <th scope="col">{{ $t('tickets.message') }}</th>
            <th scope="col">{{ $t('tickets.messages') }}</th>
            <th scope="col">{{ $t('tickets.attachments') }}</th>
            <th scope="col">{{ $t('tickets.lastActionAt') }}</th>
            <th scope="col">{{ $t('tickets.status') }}</th>
            <th scope="col">{{ $t('action') }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="ticket in tickets" :key="ticket.ticketId">
            <td>
              <p class="text-muted font-size-12">{{ formatDateString(ticket.createdAt, 'DD MMM YYYY HH:mm') }}</p>
            </td>
            <td>
              <img :src="getContactTypeIcon(ticket.ticketType)" alt="" />
            </td>
            <td>
              <div class="media cursor-pointer" @click="showUser(ticket.userId)">
                <div class="avatar-xs">
                  <img
                      :id="`ticket-user-${ticket.ticketId}`"
                      :src="require('@/assets/images/users/default-user.png')"
                      class="d-flex rounded-circle avatar-xs" />
                </div>
                <div class="media-body ms-1">
                  <div v-if="ticket.userId !== null">
                    <h5 class="font-size-12 mb-0">
                        <span
                            :class="{
                            'text-primary': ticket.userSex === 1,
                            'text-danger': ticket.userSex === 2,
                            'text-dark': ticket.userSex === 0,
                          }"
                        >
                          {{ ticket.userName || '-' }}
                        </span> {{ getAge(ticket.birth) }}</h5>
                    <small class="text-muted">#{{ ticket.userId }}</small><br>
                    <small class="text-muted">{{ ticket.email }}</small>
                  </div>
                  <h5 v-else class="text-truncate font-size-12 mb-0">{{ ticket.email }}</h5>
                </div>
              </div>
            </td>
            <td>
              <p class="text-muted mb-0">{{ ticket.message | truncate(75) }}</p>
            </td>
            <td><i class="mdi mdi-message"></i> {{ ticket.messagesCount }}</td>
            <td><i class="mdi mdi-paperclip"></i> {{ ticket.attachmentsCount }}</td>
            <td>
              {{ ticket.lastActionAt == null || ticket.lastActionAt === '' ? '-' : formatDateString(ticket.lastActionAt, 'DD MMM YYYY HH:mm') }}
            </td>
            <td>
              <span
                  class="badge"
                  :class="{
                    'bg-success': `${ticket.status}` === 'C',
                    'bg-warning': `${ticket.status}` === 'R',
                    'bg-danger': `${ticket.status}` === 'O',
                    'bg-info': `${ticket.status}` === 'W',
                    'bg-pink': `${ticket.status}` === 'A',
                  }"
              >{{ $t(`tickets.status_${ticket.status}`) }}</span
              >
            </td>
            <td>
              <b-dropdown
                  class="card-drop"
                  variant="white"
                  menu-class="dropdown-menu-end"
                  right
                  toggle-class="p-0"
              >
                <template v-slot:button-content>
                  <i class="mdi mdi-dots-horizontal font-size-18"></i>
                </template>
                <b-dropdown-item
                    href="javascript: void(0);"
                    @click="reviewTicket(ticket.ticketId)"
                >{{ $t("tickets.review") }}</b-dropdown-item>
                <b-dropdown-item
                    v-if="ticket.userId !== null"
                    href="javascript: void(0);"
                    @click="showUser(ticket.userId)"
                >{{ $t("show_user") }}</b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item v-show="ticket.status !== 'O' && ticket.status !== 'C'"
                    href="javascript: void(0);"
                    @click="closeTicket(ticket)"
                >{{ $t("close") }}</b-dropdown-item>
                <b-dropdown-item v-show="ticket.status === 'C'"
                     href="javascript: void(0);"
                     @click="reopenTicket(ticket)"
                >{{ $t("tickets.reopen") }}</b-dropdown-item>
              </b-dropdown>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && tickets.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No tickets were found for these filters. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="tickets-list"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>

    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>
  </Layout>
</template>

<style>
.cursor-pointer {
  cursor: pointer;
}

.b-sidebar-right {
  width:35%;
}

.b-sidebar-body {
  padding:16px;
}
</style>
